import { useEffect, useState, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * TeaControlCenter — settings + push history + watchedTickers in one card.
 *
 * Three sub-sections:
 *   1. SCHEDULE          — toggle scheduler, choose 5/15/30/60 min, "only critical"
 *   2. WATCHED TICKERS   — chip-style add/remove (falls back to portfolio when empty)
 *   3. PUSH HISTORY      — last 7 days of pushes, useful/noise rating buttons
 *
 * Talks to:
 *   GET /api/settings (existing)        — read current settings
 *   PUT /api/settings (existing)        — persist {teaScheduleEnabled,
 *                                          teaScheduleMinutes, teaOnlyCritical,
 *                                          watchedTickers}
 *   GET /api/omega/tea/push/history     — recent pushes + stats
 *   POST /api/omega/tea/push/{aid}/rate — rate useful/noise/null
 */

const INTERVALS = [5, 15, 30, 60];

const SENT_COLORS = {
  bullish: "#4ade80", bearish: "#f87171", mixed: "#fbbf24", neutral: "#8b949e",
};

function fmtRel(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return `${Math.round(diff)}s ago`;
    if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
    return `${Math.round(diff / 86400)}d ago`;
  } catch { return ""; }
}

export default function TeaControlCenter() {
  const [settings, setSettings] = useState(null);
  const [history, setHistory] = useState({ pushes: [], stats: { useful: 0, noise: 0, unrated: 0 } });
  const [savingKey, setSavingKey] = useState(null);
  const [tickerInput, setTickerInput] = useState("");

  const loadSettings = useCallback(async () => {
    try { const r = await axios.get(`${API}/settings`); setSettings(r.data || {}); } catch {}
  }, []);
  const loadHistory = useCallback(async () => {
    try { const r = await axios.get(`${API}/omega/tea/push/history?days=7&limit=50`); setHistory(r.data || { pushes: [] }); } catch {}
  }, []);

  useEffect(() => {
    loadSettings();
    loadHistory();
    const iv = setInterval(loadHistory, 60000);
    return () => clearInterval(iv);
  }, [loadSettings, loadHistory]);

  const saveSetting = async (key, value) => {
    setSavingKey(key);
    const prev = settings?.[key];
    setSettings(p => ({ ...(p || {}), [key]: value }));
    try {
      await axios.put(`${API}/settings`, { [key]: value });
    } catch {
      setSettings(p => ({ ...(p || {}), [key]: prev }));
    }
    setSavingKey(null);
  };

  const addTicker = (e) => {
    e?.preventDefault();
    const t = tickerInput.trim().toUpperCase();
    if (!t || !/^[A-Z0-9]{1,8}$/.test(t)) return;
    const cur = (settings?.watchedTickers || []).map(s => String(s).toUpperCase());
    if (cur.includes(t)) { setTickerInput(""); return; }
    saveSetting("watchedTickers", [...cur, t].slice(0, 20));
    setTickerInput("");
  };
  const removeTicker = (t) => {
    const cur = (settings?.watchedTickers || []).map(s => String(s).toUpperCase());
    saveSetting("watchedTickers", cur.filter(x => x !== t));
  };

  const ratePush = async (articleId, rating) => {
    setHistory(prev => ({
      ...prev,
      pushes: prev.pushes.map(p => p.article_id === articleId ? { ...p, rating } : p),
    }));
    try { await axios.post(`${API}/omega/tea/push/${articleId}/rate`, { rating }); } catch {}
    loadHistory();
  };

  const enabled = !!settings?.teaScheduleEnabled;
  const interval = INTERVALS.includes(settings?.teaScheduleMinutes) ? settings.teaScheduleMinutes : 15;
  const onlyCritical = !!settings?.teaOnlyCritical;
  const watched = settings?.watchedTickers || [];

  return (
    <div className="card" data-testid="tea-control-center" style={{
      borderColor: "rgba(167,139,250,0.26)",
      background: "linear-gradient(165deg, rgba(167,139,250,0.04), rgba(34,211,238,0.02))",
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
        <div style={{
          width: 36, height: 36, borderRadius: 11,
          background: "linear-gradient(135deg, #be185d, #831843)",
          color: "#fff", display: "grid", placeItems: "center", fontSize: 18,
          boxShadow: "0 6px 18px rgba(190,24,93,0.35)",
        }}>🍵</div>
        <div>
          <div style={{ fontSize: 9, letterSpacing: 2, color: "var(--dim)", fontWeight: 700 }}>OMEGA TEA · CONTROL CENTER</div>
          <div style={{ fontSize: 14, fontWeight: 700, color: "var(--text)" }}>Schedule, watched tickers, and push history</div>
        </div>
      </div>

      {/* ─── 1. SCHEDULE ─────────────────────────────────────────────────── */}
      <div data-testid="tea-schedule-section" style={{
        padding: 14, borderRadius: 12, marginBottom: 12,
        background: "rgba(255,255,255,0.025)", border: "1px solid rgba(255,255,255,0.06)",
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, color: "var(--text)" }}>Auto-push tea</div>
            <div style={{ fontSize: 11, color: "var(--dim)" }}>Scan critical/whale tea every {interval}m for held tickers.</div>
          </div>
          <button
            data-testid="tea-schedule-toggle"
            onClick={() => saveSetting("teaScheduleEnabled", !enabled)}
            disabled={savingKey === "teaScheduleEnabled"}
            style={{
              width: 48, height: 26, borderRadius: 999, border: "none",
              background: enabled ? "linear-gradient(135deg, #a78bfa, #be185d)" : "var(--layer2)",
              cursor: "pointer", position: "relative", flex: "0 0 auto",
              transition: "background 220ms ease",
            }}
            aria-pressed={enabled}
          >
            <span style={{
              position: "absolute", top: 3, left: enabled ? 25 : 3,
              width: 20, height: 20, borderRadius: "50%", background: "#fff",
              transition: "left 200ms ease", boxShadow: "0 2px 5px rgba(0,0,0,0.3)",
            }} />
          </button>
        </div>

        {enabled && (
          <>
            <div style={{ display: "flex", gap: 6, marginBottom: 10, flexWrap: "wrap" }}>
              {INTERVALS.map(m => (
                <button
                  key={m}
                  data-testid={`tea-interval-${m}`}
                  onClick={() => saveSetting("teaScheduleMinutes", m)}
                  style={{
                    padding: "5px 12px", borderRadius: 999,
                    border: `1px solid ${m === interval ? "rgba(167,139,250,0.55)" : "rgba(255,255,255,0.08)"}`,
                    background: m === interval ? "rgba(167,139,250,0.18)" : "rgba(255,255,255,0.03)",
                    color: m === interval ? "#a78bfa" : "var(--dim)",
                    fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
                  }}
                >{m}m</button>
              ))}
            </div>
            <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
              <input
                type="checkbox"
                data-testid="tea-only-critical"
                checked={onlyCritical}
                onChange={e => saveSetting("teaOnlyCritical", e.target.checked)}
                style={{ accentColor: "#a78bfa" }}
              />
              <span style={{ fontSize: 11, color: "var(--text)" }}>Only push <strong>CRITICAL</strong> tea (skip notable + general)</span>
            </label>
          </>
        )}
      </div>

      {/* ─── 2. WATCHED TICKERS ─────────────────────────────────────────── */}
      <div data-testid="tea-watched-section" style={{
        padding: 14, borderRadius: 12, marginBottom: 12,
        background: "rgba(255,255,255,0.025)", border: "1px solid rgba(255,255,255,0.06)",
      }}>
        <div style={{ marginBottom: 8 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: "var(--text)" }}>Watched tickers</div>
          <div style={{ fontSize: 11, color: "var(--dim)" }}>
            {watched.length === 0
              ? "Empty → falls back to your Public.com portfolio holdings."
              : `${watched.length} ticker${watched.length === 1 ? "" : "s"} — overrides portfolio fallback.`}
          </div>
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 10 }}>
          {watched.map(t => (
            <span key={t} data-testid={`tea-watched-${t}`} style={{
              display: "inline-flex", alignItems: "center", gap: 6,
              padding: "5px 11px", borderRadius: 999,
              background: "rgba(74,222,128,0.10)", color: "#4ade80",
              border: "1px solid rgba(74,222,128,0.32)",
              fontSize: 11, fontWeight: 700, letterSpacing: 0.5,
            }}>
              {t}
              <button
                data-testid={`tea-watched-remove-${t}`}
                onClick={() => removeTicker(t)}
                aria-label={`Remove ${t}`}
                style={{
                  background: "none", border: "none", color: "#4ade80",
                  cursor: "pointer", padding: 0, fontSize: 14, lineHeight: 1,
                  fontFamily: "inherit", marginLeft: 2,
                }}
              >×</button>
            </span>
          ))}
        </div>
        <form onSubmit={addTicker} style={{ display: "flex", gap: 6 }}>
          <input
            data-testid="tea-watched-input"
            value={tickerInput}
            onChange={e => setTickerInput(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 8))}
            placeholder="Add ticker (e.g. NVDA)"
            style={{
              flex: 1, padding: "8px 12px", borderRadius: 8,
              border: "1px solid rgba(255,255,255,0.10)",
              background: "rgba(255,255,255,0.03)", color: "var(--text)",
              fontSize: 12, fontFamily: "inherit", outline: "none",
              textTransform: "uppercase",
            }}
          />
          <button
            data-testid="tea-watched-add"
            type="submit"
            disabled={!tickerInput.trim()}
            style={{
              padding: "8px 14px", borderRadius: 8, border: "none",
              background: "linear-gradient(135deg, #4ade80, #047857)",
              color: "#fff", fontWeight: 700, fontSize: 11,
              cursor: tickerInput.trim() ? "pointer" : "not-allowed",
              opacity: tickerInput.trim() ? 1 : 0.5,
              fontFamily: "inherit",
            }}
          >+ Add</button>
        </form>
      </div>

      {/* ─── 3. PUSH HISTORY ────────────────────────────────────────────── */}
      <div data-testid="tea-history-section" style={{
        padding: 14, borderRadius: 12,
        background: "rgba(255,255,255,0.025)", border: "1px solid rgba(255,255,255,0.06)",
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10, gap: 8, flexWrap: "wrap" }}>
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, color: "var(--text)" }}>Push history · last 7 days</div>
            <div style={{ fontSize: 11, color: "var(--dim)" }}>
              {history.count || history.pushes.length || 0} push{(history.pushes.length || 0) === 1 ? "" : "es"}
            </div>
          </div>
          <div style={{ display: "flex", gap: 6, fontSize: 10, fontWeight: 700, letterSpacing: 0.5 }}>
            <span style={{ padding: "3px 8px", borderRadius: 6, background: "rgba(74,222,128,0.12)", color: "#4ade80", border: "1px solid rgba(74,222,128,0.3)" }}>
              ✓ {history.stats?.useful || 0}
            </span>
            <span style={{ padding: "3px 8px", borderRadius: 6, background: "rgba(248,113,113,0.10)", color: "#f87171", border: "1px solid rgba(248,113,113,0.28)" }}>
              ✕ {history.stats?.noise || 0}
            </span>
            <span style={{ padding: "3px 8px", borderRadius: 6, background: "rgba(255,255,255,0.04)", color: "var(--dim)", border: "1px solid rgba(255,255,255,0.08)" }}>
              ? {history.stats?.unrated || 0}
            </span>
          </div>
        </div>
        {(history.pushes || []).length === 0 ? (
          <div data-testid="tea-history-empty" style={{ padding: "16px 0", textAlign: "center", color: "var(--dim)", fontSize: 12 }}>
            No pushes yet — enable scheduling above or hit Test-fire.
          </div>
        ) : (
          <div style={{ display: "grid", gap: 6, maxHeight: 320, overflow: "auto" }}>
            {(history.pushes || []).map(p => {
              const sent = SENT_COLORS[p.sentiment] || SENT_COLORS.neutral;
              return (
                <div key={p.article_id} data-testid={`tea-history-${p.article_id}`} style={{
                  padding: 10, borderRadius: 9,
                  background: `${sent}0F`, border: `1px solid ${sent}26`,
                  display: "flex", alignItems: "flex-start", gap: 10,
                }}>
                  <span style={{ padding: "2px 7px", borderRadius: 4, fontSize: 9, letterSpacing: 1.2, fontWeight: 800, background: `${sent}1F`, color: sent, flex: "0 0 auto", marginTop: 2 }}>{p.ticker}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: "var(--text)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.title}</div>
                    <div style={{ fontSize: 10, color: "var(--dim)", marginTop: 2 }}>
                      {p.source || "—"} · {fmtRel(p.pushedAt)}
                      {(p.tags || []).slice(0, 2).map(tag => (
                        <span key={tag} style={{ marginLeft: 6, padding: "1px 5px", borderRadius: 3, background: "rgba(255,255,255,0.04)", fontSize: 9, letterSpacing: 0.5 }}>{tag}</span>
                      ))}
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 4, flex: "0 0 auto" }}>
                    <button
                      data-testid={`tea-rate-useful-${p.article_id}`}
                      onClick={() => ratePush(p.article_id, p.rating === "useful" ? null : "useful")}
                      title="Mark useful"
                      style={{
                        width: 26, height: 26, borderRadius: 7, border: "none", cursor: "pointer",
                        background: p.rating === "useful" ? "rgba(74,222,128,0.28)" : "rgba(255,255,255,0.04)",
                        color: p.rating === "useful" ? "#4ade80" : "var(--dim)",
                        fontSize: 12, fontFamily: "inherit",
                      }}
                    >✓</button>
                    <button
                      data-testid={`tea-rate-noise-${p.article_id}`}
                      onClick={() => ratePush(p.article_id, p.rating === "noise" ? null : "noise")}
                      title="Mark noise"
                      style={{
                        width: 26, height: 26, borderRadius: 7, border: "none", cursor: "pointer",
                        background: p.rating === "noise" ? "rgba(248,113,113,0.24)" : "rgba(255,255,255,0.04)",
                        color: p.rating === "noise" ? "#f87171" : "var(--dim)",
                        fontSize: 12, fontFamily: "inherit",
                      }}
                    >✕</button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
